import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Button, Card, CardHeader, CardBody, CardTitle,
    Row, Col, FormGroup, Label, Input, Nav, NavItem, NavLink, TabContent, TabPane,
    Badge, Spinner
} from 'reactstrap';
import ReactTable from 'components/ReactTable/ReactTable.js';
import classnames from 'classnames';
import ReactLoading from 'react-loading';
import api from '../../../api/api';
import { applyPenalty } from './service/paymentsService';
import { fetchClientsMinimal, getBookingsByClient } from './service/clientsService';
import ClientDashboard from './ClientDashboard';
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";
import '../Clients/style.css';
import './ControlClientes.css';
import { useSede } from 'context/SedeContext';
import useSedeRefresh from '../../../hooks/useSedeRefresh';

// Loading overlay styles
const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
};

const loaderContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
};

const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const normalize = (text) =>
        text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    const ControlClientes = () => {
        const { addSedeParams, addSedeHeaders } = useSede();
        const [activeTab, setActiveTab] = useState("1");

        const toggleTab = tab => {
        console.log('toggleTab called:', { from: activeTab, to: tab });
        if (activeTab !== tab) {
            setActiveTab(tab);
            // Clear bookings when switching away from tab 2 to free up memory
            if (activeTab === "2" && tab !== "2") {
                setBookings([]);
                setSelectedClientId(null);
            }
        }
        };

        const [data, setData] = useState([]);
        const [year, setYear] = useState(new Date().getFullYear());
        const [month, setMonth] = useState(new Date().getMonth() + 1);
        const [loading, setLoading] = useState(false);
        const [clients, setClients] = useState([]);
        const [selectedClientId, setSelectedClientId] = useState(null);
        const [bookings, setBookings] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [showDashboard, setShowDashboard] = useState(false);
    const [selectedClientForDashboard, setSelectedClientForDashboard] = useState(null);
    const searchTimeoutRef = useRef(null);
    const fetchTimeoutRef = useRef(null);
    const hasInitialized = useRef(false);
    const clientsLoadingRef = useRef(false); // Track if clients are being loaded
    const clientsLoadedRef = useRef(false); // Track if clients have been loaded
    const loadClientsRef = useRef(null); // Store the loadClients function
    const clientsEffectRunRef = useRef(false); // Track if the effect has run
    const componentId = useRef(Math.random().toString(36).substr(2, 9)); // Unique component ID

    const customFilterOption = useCallback((option, inputValue) => {
            return normalize(option.label).includes(normalize(inputValue));
    }, []);

    // Debounced search for better performance
    const handleSearchChange = useCallback((inputValue) => {
        setSearchTerm(inputValue);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            // Trigger search after 300ms of no typing
        }, 300);
    }, []);

    const fetchData = useCallback(async () => {
        const fetchId = Math.random().toString(36).substr(2, 9);
        console.log(`fetchData [${fetchId}]: Starting`, { 
            isFetchingData, 
            year, 
            month 
        });
        
        // Prevent multiple simultaneous requests
        if (isFetchingData) {
            console.log(`fetchData [${fetchId}]: Skipping - already fetching`);
            return;
        }

        setIsFetchingData(true);
            setLoading(true);
            try {
                console.log(`fetchData [${fetchId}]: Making API call to clases-por-mes`);
                const sedeParams = addSedeParams({ year, month });
                const sedeHeaders = addSedeHeaders({});
                const response = await api.get(`/studio/clases-por-mes/`, { params: sedeParams, headers: sedeHeaders });
                const formatted = response.data.map((item, index) => {
                    // Determine client status based on data
                    const isNewClient = item.valid_classes === 0 && item.no_show_classes === 0;
                    const needsRenewal = item.remaining_classes <= 2 && item.remaining_classes > 0;
                    const isInactive = item.remaining_classes === 0;
                    
                    let status = "Activo";
                    let statusColor = "success";
                    
                    if (isNewClient) {
                        status = "Nuevo";
                        statusColor = "info";
                    } else if (needsRenewal) {
                        status = "Renovación";
                        statusColor = "warning";
                    } else if (isInactive) {
                        status = "Inactivo";
                        statusColor = "secondary";
                    }
                    
                    return {
                        row_number: index + 1,
                    alumno: item.client_name,
                    membership: item.membership,
                    expected_classes: item.expected_classes,
                        no_show_classes: item.no_show_classes,
                        total_classes: item.expected_classes,
                    valid_classes: item.valid_classes,
                        remaining_classes: item.expected_classes - item.valid_classes,
                        renewal_date: "01/09/2025", // Placeholder - you can calculate this from payment data
                        status: status,
                        status_color: statusColor,
                        client_id: item.client_id
                    };
                });
                setData(formatted);
                console.log(`fetchData [${fetchId}]: Completed successfully`, { dataLength: formatted.length });
            } catch (err) {
                console.error(`fetchData [${fetchId}]: Error`, err);
                toast.error('Error al cargar clases');
            } finally {
                setLoading(false);
            setIsFetchingData(false);
            }
    }, [year, month]);

    const handlePenalty = useCallback(async (clientId, amount) => {
            if (!window.confirm(`¿Deseas asignar una penalización de Q${amount}?`)) return;
            try {
                await applyPenalty({ client_id: clientId, amount });
                toast.success('Penalización aplicada correctamente.');
                fetchData();
            } catch (err) {
                toast.error('Error al aplicar penalización.');
            }
    }, [fetchData]);

    const loadClients = useCallback(async () => {
        const requestId = Math.random().toString(36).substr(2, 9);
        console.log(`loadClients [${requestId}]: Starting check`, { 
            loading: clientsLoadingRef.current, 
            loaded: clientsLoadedRef.current 
        });
        
        // Prevent multiple simultaneous requests
        if (clientsLoadingRef.current || clientsLoadedRef.current) {
            console.log(`loadClients [${requestId}]: Skipping - already loading or loaded`, { 
                loading: clientsLoadingRef.current, 
                loaded: clientsLoadedRef.current 
            });
            return;
        }
        
        try {
            console.log(`loadClients [${requestId}]: Starting ultra-fast client fetch`);
            clientsLoadingRef.current = true;
            setLoadingClients(true);
            
            // Use the fast minimal endpoint directly with sede filtering
            const sedeParams = addSedeParams({});
            const sedeHeaders = addSedeHeaders({});
            const clientData = await fetchClientsMinimal(sedeParams, sedeHeaders);
            console.log(`loadClients [${requestId}]: Minimal endpoint succeeded`);
            
            console.log(`loadClients [${requestId}]: Received client data`, { count: clientData.length });
                setClients(clientData);
            clientsLoadedRef.current = true; // Mark as loaded
            } catch (err) {
            console.error(`loadClients [${requestId}]: All endpoints failed`, err);
                toast.error("Error al cargar clientes");
        } finally {
            clientsLoadingRef.current = false;
            setLoadingClients(false);
        }
    }, []);

    // Store the function in ref to avoid dependency issues
    loadClientsRef.current = loadClients;

    // Escuchar cambios de sede y refrescar automáticamente
    useSedeRefresh(loadClients, [addSedeParams, addSedeHeaders]);

    // Track component mount
    useEffect(() => {
        console.log(`ControlClientes [${componentId.current}]: Component mounted`);
        return () => {
            console.log(`ControlClientes [${componentId.current}]: Component unmounting`);
        };
    }, []);

    const loadBookings = useCallback(async () => {
            if (!selectedClientId) return;
            setLoading(true);
            try {
                const sedeParams = addSedeParams({});
                const sedeHeaders = addSedeHeaders({});
                const clientBookings = await getBookingsByClient(selectedClientId, sedeParams, sedeHeaders);
                setBookings(clientBookings);
            } catch (err) {
                toast.error("Error al cargar reservas");
            setBookings([]); // Clear bookings on error
            } finally {
                setLoading(false);
            }
    }, [selectedClientId, addSedeParams, addSedeHeaders]);

    const handleViewDashboard = useCallback((clientId, clientName) => {
        setSelectedClientForDashboard({ id: clientId, name: clientName });
        setShowDashboard(true);
    }, []);

    const handleBackFromDashboard = useCallback(() => {
        setShowDashboard(false);
        setSelectedClientForDashboard(null);
        }, []);

        useEffect(() => {
        const effectId = Math.random().toString(36).substr(2, 9);
        console.log(`useEffect fetchData [${effectId}]:`, { 
            activeTab, 
            month, 
            year, 
            hasInitialized: hasInitialized.current,
            isFetchingData
        });
        
        if (activeTab === "1") {
            // Clear any existing timeout
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }

            // For initial load, call immediately. For subsequent changes, debounce
            if (!hasInitialized.current) {
                console.log(`useEffect fetchData [${effectId}]: Initial load - calling fetchData`);
                hasInitialized.current = true;
                fetchData();
            } else {
                console.log(`useEffect fetchData [${effectId}]: Debounced load - setting timeout`);
                // Debounce the fetch to prevent multiple rapid calls
                fetchTimeoutRef.current = setTimeout(() => {
                    fetchData();
                }, 300);
            }
        }
    }, [month, year, activeTab, fetchData]);

        useEffect(() => {
        if (selectedClientId) {
            loadBookings();
        }
    }, [selectedClientId, loadBookings]);

    // Load clients when tab 2 is accessed - only once
    useEffect(() => {
        const effectId = Math.random().toString(36).substr(2, 9);
        console.log(`useEffect clients [${effectId}]:`, { 
            activeTab, 
            clientsLength: clients.length, 
            loading: clientsLoadingRef.current,
            loaded: clientsLoadedRef.current,
            effectRun: clientsEffectRunRef.current
        });
        
        if (activeTab === "2" && !clientsEffectRunRef.current) {
            console.log(`useEffect [${effectId}]: Triggering loadClients - FIRST TIME ONLY`);
            clientsEffectRunRef.current = true; // Mark as run immediately
            loadClientsRef.current?.();
        } else if (activeTab === "2" && clientsEffectRunRef.current) {
            console.log(`useEffect [${effectId}]: Tab 2 but effect already run - SKIPPING`);
        }
    }, [activeTab]); // Remove loadClients dependency

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, []);

    // Memoized data processing for better performance
    const clientOptions = useMemo(() => {
        return clients.map(c => ({
            value: c.id,
            label: c.name || `${c.first_name} ${c.last_name}`,
        }));
    }, [clients]);

    const selectedClient = useMemo(() => {
        if (!selectedClientId) return null;
        const client = clients.find(c => c.id === parseInt(selectedClientId));
        return client ? {
            value: parseInt(selectedClientId),
            label: client.name || `${client.first_name} ${client.last_name}`
        } : null;
    }, [clients, selectedClientId]);

    const bookingsTableData = useMemo(() => {
        return bookings.map(item => ({
            fecha: item.class_date,
            horario: item.schedule || "—",
            estado: item.attendance_status === 'attended' ? 'Asistió' :
                item.attendance_status === 'no_show' ? 'Inasistencia' :
                    item.attendance_status === 'cancelled' ? 'Cancelada' : 'Pendiente',
        }));
    }, [bookings]);

        // Show dashboard if selected
        if (showDashboard && selectedClientForDashboard) {
            return (
                <ClientDashboard 
                    clientId={selectedClientForDashboard.id}
                    onBack={handleBackFromDashboard}
                />
            );
        }

        return (
            <>
                {loading && (
                    <div style={overlayStyles}>
                        <div style={loaderContainerStyles}>
                            <ReactLoading type="spin" color="white" height="40%" />
                        </div>
                    </div>
                )}
                <Toaster position="top-right" />

                <div className="content">
                    <Nav tabs className="custom-tabs">
                        <NavItem>
                            <NavLink
                                className={classnames("custom-tab", { active: activeTab === "1" })}
                                onClick={() => toggleTab("1")}
                            >
                                Control por Mes
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames("custom-tab", { active: activeTab === "2" })}
                                onClick={() => toggleTab("2")}
                            >
                                Historial por Cliente
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                        <div className="p-4">
                            {/* Filter Section */}
                            <Card className="mb-4 border-0 shadow-sm fade-in">
                                <CardBody>
                                    <Row className="align-items-end">
                                <Col md={3}>
                                            <FormGroup className="mb-0">
                                                <Label className="form-label-modern">
                                                    <i className="nc-icon nc-calendar-60 mr-1"></i>
                                                    Mes
                                                </Label>
                                                <Input
                                                    type="select"
                                                    value={month}
                                                    onChange={(e) => setMonth(Number(e.target.value))}
                                                    className="form-control-modern"
                                                >
                                            {monthNames.map((name, i) => (
                                                <option key={i} value={i + 1}>{name}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                            <FormGroup className="mb-0">
                                                <Label className="form-label-modern">
                                                    <i className="nc-icon nc-calendar-60 mr-1"></i>
                                                    Año
                                                </Label>
                                                <Input
                                                    type="number"
                                                    value={year}
                                                    onChange={(e) => setYear(Number(e.target.value))}
                                                    className="form-control-modern"
                                                    min="2020"
                                                    max="2030"
                                                />
                                    </FormGroup>
                                </Col>
                                        <Col md={3}>
                                    <Button
                                                color="success"
                                                className="btn-modern w-100"
                                                outline
                                                onClick={() => {
                                                    // Clear any existing timeout
                                                    if (fetchTimeoutRef.current) {
                                                        clearTimeout(fetchTimeoutRef.current);
                                                    }
                                                    // Immediate fetch for manual search
                                                    fetchData();
                                                }}
                                                disabled={isFetchingData}
                                            >
                                                {isFetchingData ? (
                                                    <>
                                                        <Spinner size="sm" className="mr-2" />
                                                        Buscando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa fa-search mr-2"></i>
                                        Buscar
                                                    </>
                                                )}
                                    </Button>
                                </Col>
                                        <Col md={3}>
                                            <div className="text-right">
                                                <h6 className="text-muted mb-1">Período Seleccionado</h6>
                                                <h5 className="text-primary mb-0">
                                                    {monthNames[month - 1]} {year}
                                                </h5>
                                            </div>
                                </Col>
                            </Row>
                                </CardBody>
                            </Card>

                            {/* Results Section */}
                            <Card className="border-0 shadow-sm fade-in">
                                <CardHeader className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <CardTitle tag="h5" className="mb-0">
                                            <i className="nc-icon nc-paper mr-2"></i>
                                            Reporte de Clases - {monthNames[month - 1]} {year}
                                        </CardTitle>
                                        <Badge color="secondary" className="px-3 py-2">
                                            {data.length} registros
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardBody className="p-0">
                                    <ReactTable
                                        data={data}
                                        columns={[
                                            {
                                                Header: '#',
                                                accessor: 'row_number',
                                                width: 50,
                                                Cell: ({ value }) => (
                                                    <div className="text-center font-weight-bold">
                                                        {value}
                                                    </div>
                                                )
                                            },
                                            {
                                                Header: 'NOMBRE',
                                                accessor: 'alumno',
                                                minWidth: 150,
                                                Cell: ({ value }) => (
                                                    <div className="font-weight-medium">
                                                        {value}
                                                    </div>
                                                )
                                            },
                                            {
                                                Header: 'PAQUETE',
                                                accessor: 'membership',
                                                width: 120,
                                                Cell: ({ value }) => (
                                                    <div className="text-center small">
                                                        {value}
                                                    </div>
                                                )
                                            },
                                            {
                                                Header: 'CLASES ESPERADAS',
                                                accessor: 'expected_classes',
                                                width: 80,
                                                Cell: ({ value }) => (
                                                    <div className="text-center font-weight-bold">
                                                        {value}
                                                    </div>
                                                )
                                            },
                                            {
                                                Header: 'TOMADAS',
                                                accessor: 'valid_classes',
                                                width: 70,
                                                Cell: ({ value }) => (
                                                    <div className="text-center text-success font-weight-bold">
                                                        {value}
                                                    </div>
                                                )
                                            },
                                            {
                                                Header: 'INASISTENCIAS',
                                                accessor: 'no_show_classes',
                                                width: 80,
                                                Cell: ({ value }) => (
                                                    <div className="text-center">
                                                        <span className={`badge ${value > 0 ? 'badge-danger' : 'badge-light'}`}>
                                                            {value}
                                                        </span>
                                                    </div>
                                                )
                                            },
                                            {
                                                Header: 'RESTANTES',
                                                accessor: 'remaining_classes',
                                                width: 80,
                                                Cell: ({ value }) => (
                                                    <div className="text-center font-weight-bold text-primary">
                                                        {value}
                                                    </div>
                                                )
                                            },
                                            {
                                                Header: 'RENOVACIÓN',
                                                accessor: 'renewal_date',
                                                width: 100,
                                                Cell: ({ value }) => (
                                                    <div className="text-center small">
                                                        {value}
                                                    </div>
                                                )
                                            },
                                            {
                                                Header: 'ESTADO',
                                                accessor: 'status',
                                                width: 120,
                                                Cell: ({ row }) => {
                                                    const status = row.original.status;
                                                    const statusColor = row.original.status_color;
                                                    const statusIcons = {
                                                        "Activo": "fa-check-circle",
                                                        "Nuevo": "fa-user-plus",
                                                        "Renovación": "fa-exclamation-triangle",
                                                        "Inactivo": "fa-times-circle"
                                                    };
                                                    return (
                                                        <div className="text-center">
                                                            <span className={`badge badge-${statusColor} d-flex align-items-center justify-content-center`}>
                                                                <i className={`fa ${statusIcons[status]} mr-1`}></i>
                                                                {status}
                                                            </span>
                                                        </div>
                                                    );
                                                }
                                            },
                                            {
                                                Header: 'DASHBOARD',
                                                accessor: 'dashboard',
                                                width: 100,
                                                sortable: false,
                                                filterable: false,
                                                Cell: ({ row }) => (
                                                    <div className="text-center">
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={() => handleViewDashboard(row.original.client_id, row.original.alumno)}
                                                            className="btn-dashboard"
                                                        >
                                                            <i className="fa fa-chart-line mr-1"></i>
                                                            Ver
                                                        </Button>
                                                    </div>
                                                )
                                            }
                                        ]}
                                        showPagination={true}
                                        className="table-compact"
                                        defaultPageSize={20}
                                        pageSizeOptions={[10, 20, 30, 50]}
                                    />
                                </CardBody>
                            </Card>
                        </div>
                        </TabPane>

                        <TabPane tabId="2">
                        <div className="p-4">
                            {/* Client Selection Section */}
                            <Card className="mb-4 border-0 shadow-sm fade-in">
                                <CardHeader className="bg-light">
                                    <CardTitle tag="h5" className="mb-0">
                                        <i className="nc-icon nc-single-02 mr-2"></i>
                                        Seleccionar Cliente
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                            <Row>
                                        <Col md={6}>
                                    <FormGroup>
                                                <Label className="form-label-modern">
                                                    <i className="fa fa-search mr-1"></i>
                                                    Buscar Alumno
                                                </Label>
                                        <Select
                                                    options={clientOptions}
                                                    value={selectedClient}
                                                onChange={(selected) => {
                                                    setSelectedClientId(selected ? selected.value : "");
                                                }}
                                                    placeholder={loadingClients ? "Cargando clientes..." : "Selecciona un alumno..."}
                                            isClearable
                                                    isLoading={loadingClients}
                                            filterOption={customFilterOption}
                                                    onFocus={() => {
                                                        // Just log - clients are loaded by useEffect when tab is accessed
                                                        console.log('onFocus triggered - clients already loaded:', { 
                                                            clientsLength: clients.length, 
                                                            loading: clientsLoadingRef.current 
                                                        });
                                                    }}
                                                    className="react-select-modern"
                                                    classNamePrefix="react-select"
                                        />
                                    </FormGroup>
                                </Col>
                                        <Col md={6}>
                                            {selectedClient && (
                                                <div className="d-flex align-items-center h-100">
                                                    <div className="client-info-card p-3 bg-light rounded">
                                                        <div className="d-flex align-items-center">
                                                            <div className="avatar-lg bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mr-3">
                                                                {selectedClient.label.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-1">{selectedClient.label}</h6>
                                                                <small className="text-muted">Cliente seleccionado</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                </Col>
                            </Row>
                                </CardBody>
                            </Card>

                            {/* Bookings History Section */}
                            {selectedClientId ? (
                                <Card className="border-0 shadow-sm">
                                    <CardHeader className="bg-light">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <CardTitle tag="h5" className="mb-0">
                                                <i className="nc-icon nc-calendar-60 mr-2"></i>
                                                Historial de Reservas
                                            </CardTitle>
                                            <Badge color="info" className="px-3 py-2">
                                                {bookings.length} reservas
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    <CardBody className="p-0">
                                        <ReactTable
                                            data={bookingsTableData}
                                            columns={[
                                                {
                                                    Header: "📅 Fecha de Clase",
                                                    accessor: "fecha",
                                                    Cell: ({ value }) => (
                                                        <div className="text-center">
                                                            <span className="badge badge-light px-3 py-2">
                                                                {value}
                                                            </span>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    Header: "🕐 Horario",
                                                    accessor: "horario",
                                                    Cell: ({ value }) => (
                                                        <div className="text-center">
                                                            <span className="badge badge-info px-3 py-2">
                                                                {value}
                                                            </span>
                                                        </div>
                                                    )
                                                },
                                                {
                                                    Header: "📊 Estado",
                                                    accessor: "estado",
                                                    Cell: ({ value }) => {
                                                        const statusConfig = {
                                                            "Asistió": {
                                                                icon: "fa-check",
                                                                color: "success",
                                                                bgColor: "badge-success"
                                                            },
                                                            "Inasistencia": {
                                                                icon: "fa-times",
                                                                color: "danger",
                                                                bgColor: "badge-danger"
                                                            },
                                                            "Cancelada": {
                                                                icon: "fa-ban",
                                                                color: "warning",
                                                                bgColor: "badge-warning"
                                                            },
                                                            "Pendiente": {
                                                                icon: "fa-clock",
                                                                color: "secondary",
                                                                bgColor: "badge-secondary"
                                                            }
                                                        };
                                                        const config = statusConfig[value] || statusConfig["Pendiente"];
                                                        return (
                                                            <div className="text-center">
                                                                <span className={`badge ${config.bgColor} px-3 py-2`}>
                                                                    <i className={`fa ${config.icon} mr-1`}></i>
                                                                    {value}
                                                            </span>
                                                            </div>
                                                        );
                                                    }
                                                }
                                            ]}
                                            className="table-modern"
                                            defaultPageSize={10}
                                            showPagination={true}
                                        />
                                    </CardBody>
                                </Card>
                            ) : (
                                <Card className="border-0 shadow-sm">
                                    <CardBody className="text-center py-5">
                                        <div className="empty-state">
                                            <i className="nc-icon nc-single-02 text-muted" style={{ fontSize: '4rem' }}></i>
                                            <h5 className="mt-3 text-muted">Selecciona un Cliente</h5>
                                            <p className="text-muted">Elige un alumno del dropdown para ver su historial de reservas</p>
                                        </div>
                                    </CardBody>
                                </Card>
                            )}
                        </div>
                        </TabPane>
                    </TabContent>
                </div>
            </>
        );
    };

    export default ControlClientes;